import { type OnboardingChatResults } from '@fruitful/types';
import React, { useMemo } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import {
    BodyLargeEmphasized,
    ScrollView,
    useWindowDimensions,
    YStack,
} from 'smoothie';

import {
    ConversationalMMAnalyticsEvent,
    type ConversationalMMTrackEvent,
} from '../analytics/events';
import { SlideNav } from '../components/SlideNav';
import {
    ConversationalMMProvider,
    useConversationalMMContext,
} from '../context/ConversationalMMContext';
import FullscreenResultsContent from '../fullscreen/FullscreenResultsContent';
import { type TerminalSlideAction } from '../types';

export interface ConversationalMMEmbeddedResultsProps {
    results: OnboardingChatResults;
    onResultsChange?: (results: OnboardingChatResults) => void;
    /** Defaults to a passive preview with no terminal-slide CTA. */
    terminalAction?: TerminalSlideAction;
    showGuidedTooltips?: boolean;
    onTrackEvent?: ConversationalMMTrackEvent;
    /** When true, always render the mobile/narrow layout regardless of window width. */
    forceNarrow?: boolean;
}

const EmbeddedResultsContentLegacy = () => {
    const {
        ahaResult,
        slideIndex,
        setSlideIndex,
        slides,
        slideCount,
        lastIndex,
        onTrackEvent,
    } = useConversationalMMContext();

    const trackPrevNext = (
        eventName:
            | typeof ConversationalMMAnalyticsEvent.ResultsPrev
            | typeof ConversationalMMAnalyticsEvent.ResultsNext,
    ) => {
        const fromSlide = slides[slideIndex];
        onTrackEvent(eventName, {
            fromSlide: fromSlide?.name ?? '',
            fromSlideIndex: slideIndex,
        });
    };

    const onPrevious = () => {
        trackPrevNext(ConversationalMMAnalyticsEvent.ResultsPrev);
        setSlideIndex(Math.max(0, slideIndex - 1));
    };
    const onNext = () => {
        trackPrevNext(ConversationalMMAnalyticsEvent.ResultsNext);
        setSlideIndex(Math.min(lastIndex, slideIndex + 1));
    };

    const ActiveSlide = slides[slideIndex]?.Component ?? null;

    const { width } = useWindowDimensions();

    const maxWidth = useMemo(() => (width > 770 ? 560 : width - 32), [width]);

    return (
        <YStack
            flex={1}
            minHeight={0}
            gap={12}
            maxWidth={maxWidth}
            alignSelf="center"
        >
            <YStack gap={4} paddingHorizontal={4}>
                <BodyLargeEmphasized textAlign="center">
                    {ahaResult.displayHeadline}
                    {ahaResult.type === 'investing'
                        ? ` ${ahaResult.projectedDate}`
                        : ahaResult.debtFreeDate !== '' &&
                          ` ${ahaResult.debtFreeDate}`}
                </BodyLargeEmphasized>
                <SlideNav
                    index={slideIndex}
                    onNext={onNext}
                    onPrevious={onPrevious}
                    slideCount={slideCount}
                    lastIndex={lastIndex}
                />
            </YStack>

            <ScrollView flex={1}>
                <YStack
                    padding={12}
                    paddingTop={0}
                    alignItems="center"
                    width="100%"
                >
                    {ActiveSlide != null && <ActiveSlide />}
                </YStack>
            </ScrollView>
        </YStack>
    );
};

const ZERO_METRICS = {
    insets: { top: 0, bottom: 0, left: 0, right: 0 },
    frame: { x: 0, y: 0, width: 0, height: 0 },
};

const EmbeddedResultsContent = ({
    forceNarrow = false,
}: {
    forceNarrow?: boolean;
}) => {
    return (
        <SafeAreaProvider initialMetrics={ZERO_METRICS}>
            <YStack
                width="100%"
                style={{ height: 'calc(66vh + 88px)' }}
                backgroundColor="$gray200"
                borderRadius={16}
                overflow="hidden"
            >
                <FullscreenResultsContent
                    skipCultivating
                    forceNarrow={forceNarrow}
                />
            </YStack>
        </SafeAreaProvider>
    );
};

/**
 * Inline/embedded rendering of the conversational money map results —
 * headline + compact nav + the real slide content, with no full-screen
 * takeover, no Cultivating hold, and no swipe-gesture chrome. Used by
 * grove-app's admin previews; also available to member-app for any
 * non-modal usage.
 */
export const ConversationalMMEmbeddedResults = ({
    results,
    onResultsChange,
    terminalAction = { mode: 'none' },
    showGuidedTooltips = false,
    onTrackEvent,
    forceNarrow = false,
}: ConversationalMMEmbeddedResultsProps) => {
    const { version } = results.artifactData;

    return (
        <ConversationalMMProvider
            results={results}
            onResultsChange={onResultsChange}
            terminalAction={terminalAction}
            showGuidedTooltips={showGuidedTooltips}
            onTrackEvent={onTrackEvent}
        >
            {version === 2 ? (
                <EmbeddedResultsContent forceNarrow={forceNarrow} />
            ) : (
                <EmbeddedResultsContentLegacy />
            )}
        </ConversationalMMProvider>
    );
};
